import { CountryCode } from "@redotech/locale/countries";
import { Currency } from "@redotech/money/iso4217/currencies";
import {
  Collection,
  ExtendedWarrantySettings,
  Offering,
  generateNoCoverageOffering,
} from "../extended-warranty";
import { PricingRuleSet } from "../team";

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function determineRuleSet(
  pricingRuleSets: PricingRuleSet[],
  country: string | null,
) {
  for (const priceSet of pricingRuleSets) {
    if (
      country === null ||
      priceSet.countries.includes(country) ||
      priceSet.countries.length == 0
    ) {
      return priceSet;
    }
  }
  return null;
}

function getApplicableRule(ruleSet: PricingRuleSet, cartTotal: number) {
  if (!cartTotal) {
    return ruleSet.priceBrackets[0];
  }
  for (let i = 0; i < ruleSet.priceBrackets.length; i++) {
    if (i === ruleSet.priceBrackets.length - 1) {
      return ruleSet.priceBrackets[i];
    }

    if (
      cartTotal > ruleSet.priceBrackets[i].pricePoint &&
      cartTotal <= ruleSet.priceBrackets[i + 1].pricePoint
    ) {
      return ruleSet.priceBrackets[i];
    }
  }
  return ruleSet.priceBrackets[ruleSet.priceBrackets.length - 1];
}

export function getOfferingPrice(
  cartTotal: number,
  pricingRuleSets: PricingRuleSet[] | undefined,
  country: string | undefined | null,
  minPrice: number,
  maxPrice: number,
  backupPercentage: number,
) {
  if (
    country === undefined ||
    !maxPrice ||
    (!backupPercentage && !pricingRuleSets?.length)
  ) {
    return 0;
  }

  if (!pricingRuleSets?.length) {
    return clamp(cartTotal * backupPercentage, minPrice, maxPrice);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const price =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return clamp(price, minPrice, maxPrice);
  }

  return parseFloat(
    clamp(cartTotal * backupPercentage, minPrice, maxPrice).toFixed(2),
  );
}

export const calculateOfferings = ({
  collectionOfferings,
  variant,
  currency = Currency.USD,
  countryCode = "US",
  coverageDisabledLabel,
}: {
  collectionOfferings: ExtendedWarrantySettings["collectionSettings"];
  variant: { price: number; variantId: string; collections: Collection[] };
  currency?: Currency;
  countryCode?: CountryCode;
  coverageDisabledLabel?: string;
}): Offering[] => {
  const matchingCollections = collectionOfferings.filter((c) =>
    variant.collections.some(
      (col) =>
        col.handle === c.collectionHandle ||
        col.title.toLowerCase() === c.collectionName.toLowerCase(),
    ),
  );
  if (matchingCollections.length === 0) {
    return [];
  }
  // if product is in more than one collection that offers an extended warranty, pick the first one for now.
  const collection = matchingCollections[0];
  if (!collection) {
    return [];
  }

  const offerings: Offering[] = collection.offerings.map((offering) => {
    const amount = getOfferingPrice(
      variant.price / 100,
      [offering.pricingRuleSet],
      countryCode,
      Number(offering.minPrice),
      Number(offering.maxPrice),
      0,
    );
    return {
      title: offering.timeTitle,
      id: offering.timeId,
      collectionHandle: collection.collectionHandle,
      price: { currency, amount },
    };
  });
  offerings.sort((a, b) => {
    return a.title.localeCompare(b.title);
  });
  offerings.push(
    generateNoCoverageOffering(
      collection.collectionHandle,
      currency,
      coverageDisabledLabel,
    ),
  );

  return offerings;
};
